/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useRef, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

// Component imports
import { Avatar, Button, Col, Form, Input, List, Popover, Row, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import OrgCreateModal from '../Modals/OrgCreateModal';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { NAV_DASHBOARD } from '../../constants/navigation';
import { setOrganization } from '../../store/auth/authActions';
import { updateRecentBots } from '../../store/bots/botsActions';

import OrgPlaceholderImage from '../../assets/placeholder_organization.svg';
import { IS_LIVECHAT_CONSOLE_ONLY } from '../../constants/env';

const OrgSelector = ({ ...props }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orgId } = useParams();
  const [orgSearchForm] = Form.useForm();
  const { t } = useTranslation();
  const orgSearchRef = useRef(null);

  const { currentUser } = useSelector((state) => state.auth);

  const [, updateState] = useState(); // Hook to force a component re-render
  const [isOrgPopoverOpen, setIsOrgPopoverOpen] = useState(false);
  const [isOrgCreateModalOpen, setIsOrgCreateModalOpen] = useState(false);

  const currentOrg = (currentUser?.organizations || []).find(org => org.id === orgId);

  const onArrowPress = useCallback((e) => {
    if (!isOrgPopoverOpen) {
      return;
    }
    // Only prevent default if the user is pressing an arrow key or enter
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
    } else {
      return;
    }
    // Capture the key and init the index
    const { key } = e;
    let index = -1;

    const orgItems = document.querySelectorAll('.org-item');
    index = Array.from(orgItems).findIndex(el => el.classList.contains('selected-org-item'));
    if (index >= 0) {
      orgItems[index].classList.remove('selected-org-item');
    } else if (key === 'Enter') {
      return;
    }

    switch (key) {
      case 'ArrowUp':
        index -= 1;
        break;
      case 'ArrowDown':
        index += 1;
        break;
      default:
        break;
    }
    // We don't allow negative selections
    if (index < 0) {
      index = 0;
    }
    // No selection over item count either
    if (index >= orgItems.length) {
      index = orgItems.length - 1;
    }
    orgItems[index]?.classList.add('selected-org-item');
    orgItems[index]?.scrollIntoView({ block: 'center' });
    // On enter select the item and close the popup
    if (key === 'Enter') {
      document.querySelector('.selected-org-item a')?.click();
    }
  }, [isOrgPopoverOpen]);

  useEffect(() => {
    window.addEventListener('keydown', onArrowPress);
    return () => {
      window.removeEventListener('keydown', onArrowPress);
    };
  }, [isOrgPopoverOpen]);

  const renderOrganizationsPopover = () => {
    const orgsDatasource = currentUser?.organizations?.length > 0
      ? currentUser.organizations
        .filter(org => (org.displayName?.toLowerCase().includes(orgSearchForm.getFieldValue('organizationSearch')?.toLowerCase() || '') || !org.displayName)) // Filter out current org
        .sort((a, b) => a.displayName?.localeCompare(b.displayName))
      : [];

    return currentUser?.organizations?.length > 0 ? (
      <Row>
        <Col span={24}>
          <Form.Item
            name="organizationSearch"
            className="mb-2"
          >
            <Input
              ref={orgSearchRef}
              size="large"
              autoComplete="off"
              placeholder={t('Search workspaces')}
              onChange={() => document.querySelector('.selected-org-item')?.classList.remove('selected-org-item')}
              prefix={<SearchOutlined className="text-secondary" />}
              onKeyDown={(e) => {
                if (e.key.includes('Arrow')) {
                  onArrowPress(e);
                }
              }}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Typography.Link
            strong
            onClick={() => {
              setIsOrgCreateModalOpen(true);
              setIsOrgPopoverOpen(false);
            }}
          >
            <Space hidden={IS_LIVECHAT_CONSOLE_ONLY} className="my-2 ml-2">
              <PlusIcon />
              {t('Create New Workspace')}
            </Space>
          </Typography.Link>
        </Col>

        <Col span={24}>
          {
            orgsDatasource.length > 0 && (
              <List
                dataSource={orgsDatasource}
                renderItem={org => (
                  <List.Item
                    key={org.id}
                    className="p-0 border-0 mb-1 org-item"
                  >
                    <Button
                      type="text"
                      size="large"
                      block
                      className={classNames('d-flex px-2 fw-normal', {
                        'bg-active': org.id === orgId
                      })}
                    >
                      <Link
                        className="w-100"
                        to={replaceUrlParams(NAV_DASHBOARD, { orgId: org.id })}
                        onClick={() => {
                          setIsOrgPopoverOpen(false);
                          if (org.id && orgId !== org.id) {
                            dispatch(setOrganization(org.id, navigate));
                            dispatch(updateRecentBots([]));
                          }
                        }}>
                        <span className="d-flex align-items-center">
                          <Avatar
                            size={24}
                            shape="square"
                            src={org.image ?? OrgPlaceholderImage}
                            className="bordered flex-none"
                          />
                          <Typography.Text ellipsis={true} className="ml-3 fs-lg">
                            {org.displayName ?? <Typography.Text type="secondary">{t('(no name)')}</Typography.Text>}
                          </Typography.Text>
                        </span>
                      </Link>
                    </Button>
                  </List.Item>
                )}
              />
            )
          }

          {
            orgsDatasource.length === 0 && (
              <Row>
                <Col span={24}>
                  <Typography.Text type="secondary" className="text-center">
                    {t('No organizations found')}
                  </Typography.Text>
                </Col>
              </Row>
            )
          }
        </Col>
      </Row>
    ) : (
      <Typography.Link
        strong
        onClick={() => {
          setIsOrgCreateModalOpen(true);
          setIsOrgPopoverOpen(false);
        }}
      >
        <Space hidden={IS_LIVECHAT_CONSOLE_ONLY} className="my-2 ml-2">
          <PlusIcon />
          {t('Create New Workspace')}
        </Space>
      </Typography.Link>
    );
  };

  return (
    <Fragment>
      <Form
        name="orgSearchForm"
        form={orgSearchForm}
        onFieldsChange={(_, allFields) => {
          updateState(allFields);
        }}
        {...props}
      >
        <Popover
          showArrow={false}
          content={renderOrganizationsPopover}
          overlayInnerStyle={{ maxWidth: 300, maxHeight: 365, marginTop: -10, ...( currentUser?.organizations.length > 5 && { overflowY: 'scroll' }) }}
          overlayClassName="bots-menu-popup"
          onOpenChange={isOpen => {
            setIsOrgPopoverOpen(isOpen);
            if (isOpen) {
              document.querySelector('.selected-org-item')?.classList.remove('selected-org-item');
              setTimeout(() => {
                orgSearchRef?.current?.focus();
              }, [150]);
            }
          }}
          trigger="click"
          open={isOrgPopoverOpen}
        >
          <Button type="text" className="h-100 p-2 bg-layout">
            <Space size={10}>
              <Avatar
                size={20}
                src={currentOrg?.image ?? OrgPlaceholderImage}
                shape="square"
              />
              <Typography.Text className="fs-12">
                {
                  currentOrg ? (
                    currentOrg?.displayName || <Typography.Text type="secondary">{t('(no name)')}</Typography.Text>
                  ) : t('Choose a Workspace')
                }
              </Typography.Text>
            </Space>
          </Button>
        </Popover>
      </Form>

      <OrgCreateModal isModalOpen={isOrgCreateModalOpen} closeModal={() => setIsOrgCreateModalOpen(false)} />
    </Fragment>
  );
};

export default OrgSelector;
